import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Sparkles, X, Send, Loader2, Bot } from 'lucide-react';
import api from '../../services/api';

const renderText = (text) => {
  // Rendu léger : **gras** + sauts de ligne
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} className="text-[#C4A052]">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
};

export const AiAssistantWidget = ({ project }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(`assistant-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const res = await api.post('/assistant/chat', {
        project,
        message: msg,
        session_id: sessionRef.current,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.answer }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: `❌ Erreur : ${e.response?.data?.detail || e.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Bouton flottant */}
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="ai-assistant-fab"
        title="Assistant IA"
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-[#C4A052] hover:bg-[#D4B86A] text-black shadow-lg shadow-[#C4A052]/30 flex items-center justify-center transition-transform hover:scale-110"
      >
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </button>

      {/* Panneau chat */}
      {open && (
        <div
          data-testid="ai-assistant-panel"
          className="fixed bottom-24 right-6 z-50 w-[400px] max-w-[calc(100vw-3rem)] h-[540px] max-h-[70vh] bg-zinc-950 border border-[#C4A052]/30 rounded-xl shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/80 flex items-center gap-2">
            <Bot className="h-5 w-5 text-[#C4A052]" />
            <div className="flex-1">
              <p className="text-sm font-bold text-white">Assistant IA</p>
              <p className="text-[10px] text-zinc-400">Projet : {project} · Lecture seule</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-xs text-zinc-400 space-y-2 p-2">
                <p className="text-zinc-300 font-semibold">💡 Exemples :</p>
                <button onClick={() => setInput('Rapport M21')} className="block w-full text-left px-3 py-2 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 transition-colors" data-testid="ai-assistant-example-1">📊 Rapport M21</button>
                <button onClick={() => setInput('Rapport global du projet')} className="block w-full text-left px-3 py-2 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 transition-colors" data-testid="ai-assistant-example-2">🌍 Rapport global du projet</button>
                <button onClick={() => setInput('Quels matchs ont un delta négatif (manque de stock) ?')} className="block w-full text-left px-3 py-2 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 transition-colors" data-testid="ai-assistant-example-3">⚖️ Matchs en manque de stock</button>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`ai-assistant-message-${i}`}
                className={m.role === 'user'
                  ? 'ml-8 bg-[#C4A052]/15 border border-[#C4A052]/30 rounded-lg px-3 py-2 text-sm text-white'
                  : 'mr-4 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-200 whitespace-pre-wrap'}
              >
                {renderText(m.content)}
              </div>
            ))}
            {loading && (
              <div className="mr-4 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-400 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-[#C4A052]" /> Analyse des données...
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="p-3 border-t border-zinc-800 bg-zinc-900/60 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder='Ex : "rapport M21"...'
              data-testid="ai-assistant-input"
              className="flex-1 bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#C4A052]/60"
            />
            <Button
              onClick={send}
              disabled={loading || !input.trim()}
              data-testid="ai-assistant-send"
              className="bg-[#C4A052] hover:bg-[#D4B86A] text-black px-3"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
